import { useEffect, useRef, useState } from 'react';
import { WebSocketAdapter } from '../utils/WebSocketAdapter';
import { Question, QuestionResult, GameStatistics, GameDisplayRoomState } from '../types/game';
import type { LeaderboardEntry } from './useGameState';
import { LiveAnswer } from '../components/shared';
import { calculateTimeRemaining, getQuestionId, isNewQuestion } from '../utils/gameUtils';
import { HINT_DELAY, STANDINGS_UPDATE_DELAY } from '../constants/gameConstants';
import type { HostPlayingState } from './useGameState';

interface UseGameSocketEventsProps {
  socket: WebSocketAdapter;
  roomState: GameDisplayRoomState;
  hostIsPlaying: boolean;
  myPlayerId?: string;
  currentQuestion: Question | null;
  previousQuestionIdRef: React.MutableRefObject<string | undefined>;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<Question | null>>;
  setTimeRemaining: React.Dispatch<React.SetStateAction<number>>;
  setQuestionResult: React.Dispatch<React.SetStateAction<QuestionResult | null>>;
  setShouldUpdateStandings: React.Dispatch<React.SetStateAction<boolean>>;
  setLeaderboardFromResult: React.Dispatch<React.SetStateAction<LeaderboardEntry[] | null>>;
  setGameEnded: React.Dispatch<React.SetStateAction<boolean>>;
  setFinalLeaderboard: React.Dispatch<React.SetStateAction<Array<{ playerId: string; playerName: string; score: number }>>>;
  setGameStatistics: React.Dispatch<React.SetStateAction<GameStatistics | null>>;
  setWaitingForNewGame: React.Dispatch<React.SetStateAction<boolean>>;
  setShowHint: React.Dispatch<React.SetStateAction<boolean>>;
  setHostPlayingState: React.Dispatch<React.SetStateAction<HostPlayingState>>;
  resetHostPlayingState: () => void;
  roomNameRef: React.MutableRefObject<string>;
}

interface UseGameSocketEventsReturn {
  liveAnswers: LiveAnswer[];
}

/**
 * Hook to manage socket event handlers for game events
 */
export const useGameSocketEvents = ({
  socket,
  roomState,
  hostIsPlaying,
  myPlayerId,
  currentQuestion,
  previousQuestionIdRef,
  setCurrentQuestion,
  setTimeRemaining,
  setQuestionResult,
  setShouldUpdateStandings,
  setLeaderboardFromResult,
  setGameEnded,
  setFinalLeaderboard,
  setGameStatistics,
  setWaitingForNewGame,
  setShowHint,
  setHostPlayingState,
  resetHostPlayingState,
  roomNameRef,
}: UseGameSocketEventsProps): UseGameSocketEventsReturn => {
  const [liveAnswersState, setLiveAnswersState] = useState<LiveAnswer[]>([]);
  const hintShownRef = useRef(false);
  const lastLiveAnswersQuestionIdRef = useRef<string | undefined>(undefined);

  // Keep live answers scoped to the active question, regardless of whether
  // question hydration came from 'question' event or room-state sync.
  useEffect(() => {
    const questionId = currentQuestion ? getQuestionId(currentQuestion) : undefined;
    if (questionId !== lastLiveAnswersQuestionIdRef.current) {
      setLiveAnswersState([]);
      lastLiveAnswersQuestionIdRef.current = questionId;
    }
  }, [currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: Question) => {
      console.log('GameDisplay received question:', data);
      const newQuestionId = getQuestionId(data);
      
      // Calculate remaining time
      const calculatedTimeRemaining = calculateTimeRemaining(data, data.startTime);
      
      if (data.timeRemaining !== undefined) {
        console.log(`GameDisplay: Using backend-calculated remaining time: ${calculatedTimeRemaining}ms`);
      } else if (data.startTime) {
        const elapsed = Date.now() - data.startTime;
        console.log(`GameDisplay: Calculated remaining time from startTime: ${calculatedTimeRemaining}ms (elapsed: ${elapsed}ms, limit: ${data.timeLimit}ms)`);
      } else {
        console.log(`GameDisplay: Using full timeLimit: ${calculatedTimeRemaining}ms`);
      }
      
      // Only update if it's a different question
      if (isNewQuestion(newQuestionId, previousQuestionIdRef.current)) {
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(data);
        setQuestionResult(null);
        setTimeRemaining(calculatedTimeRemaining);
        // Reset hint for new question
        setShowHint(false);
        hintShownRef.current = false;
        // Reset host playing state and live answers for new question.
        // Do not clear leaderboardFromResult here: keep previous question's final scores so the
        // total column does not flicker to stale room-state (e.g. 250 → 150 → 250). Next
        // question-result will overwrite with the correct leaderboard.
        resetHostPlayingState();
        setLiveAnswersState([]);
        
        // For collaborative puzzles, show hint after delay
        if (data.isCollaborative && data.hint) {
          setTimeout(() => {
            if (getQuestionId(data) === (previousQuestionIdRef.current || newQuestionId)) {
              setShowHint(true);
              hintShownRef.current = true;
            }
          }, HINT_DELAY);
        }
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      // Don't show question result screen, just clear the question
      setQuestionResult(null);
      // Use leaderboard from result so standings show new scores immediately (avoids prod race where
      // room-state arrives after we reset shouldUpdateStandings, so green points never show).
      setLeaderboardFromResult(data.leaderboard);
      // Trigger score update in StandingsSidebar (green +N pts when score increased)
      setShouldUpdateStandings(true);
      // Reset after a short delay so next question can trigger again
      setTimeout(() => {
        setShouldUpdateStandings(false);
      }, STANDINGS_UPDATE_DELAY);
      // Don't clear currentQuestion - let it show until next question arrives
    };

    const handleGameEnded = (data: { 
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      console.log('GameDisplay: game-ended event received', data);
      setGameEnded(true);
      setFinalLeaderboard(data.leaderboard);
      setGameStatistics(data.statistics || null);
      console.log('GameDisplay: Statistics set to', data.statistics);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setWaitingForNewGame(false);
    };
    
    const handleGameStarted = () => {
      console.log('GameDisplay: game-started event received');
      setWaitingForNewGame(false);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setLiveAnswersState([]);
      lastLiveAnswersQuestionIdRef.current = undefined;
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
      // Request room state to ensure we get the first question - use ref to get latest roomName
      socket.emit('get-room-state', { roomName: roomNameRef.current });
    };
    
    // Handle answer-received for host playing mode
    const handleAnswerReceived = (data: { isCorrect: boolean; points: number; responseTime: number }) => {
      console.log('GameDisplay answer-received:', data);
      setHostPlayingState(prev => ({
        ...prev,
        isSubmitting: false,
        answerSubmitted: true,
        answerCorrect: data.isCorrect,
        waitingForOthers: true,
      }));
    };

    // Handle live answers from players
    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswersState((prev) => {
        // Don't add duplicate answers from the same player
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('player-answered', handlePlayerAnswered);

    return () => {
      // Pass specific handler to only remove THIS component's listener, not App.tsx's
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('player-answered', handlePlayerAnswered);
    };
  }, [
    socket,
    hostIsPlaying,
    myPlayerId,
    currentQuestion,
    previousQuestionIdRef,
    setCurrentQuestion,
    setTimeRemaining,
    setQuestionResult,
    setShouldUpdateStandings,
    setLeaderboardFromResult,
    setGameEnded,
    setFinalLeaderboard,
    setGameStatistics,
    setWaitingForNewGame,
    setShowHint,
    setHostPlayingState,
    resetHostPlayingState,
    roomNameRef,
  ]);

  return {
    liveAnswers: liveAnswersState,
  };
};
